import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import InsertPhotoRoundedIcon from "@mui/icons-material/InsertPhotoRounded";
import {
  fileLabelFromAsset,
  resolveLandingAssetUrl,
} from "../../../utils/landingAssets";

export default function EditableImage({
  value,
  label,
  helperText = "",
  height = 180,
  disabled = false,
  accept = "image/svg+xml,image/png,image/jpeg,image/webp,.svg,.png,.jpg,.jpeg,.webp",
  onUpload,
  onRemove,
}) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const imageUrl = resolveLandingAssetUrl(value);

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      setBusy(true);
      setError("");
      await onUpload?.(file);
    } catch (err) {
      setError(err?.message || "No se pudo subir la imagen");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      setBusy(true);
      setError("");
      await onRemove?.();
    } catch (err) {
      setError(err?.message || "No se pudo eliminar la imagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={1}>
      <Typography sx={{ fontWeight: 900, color: "#19242F" }}>{label}</Typography>
      <Box
        sx={{
          position: "relative",
          minHeight: height,
          borderRadius: 2.5,
          overflow: "hidden",
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(25,36,47,.045)",
          border: "1px dashed rgba(25,36,47,.18)",
        }}
      >
        {imageUrl ? (
          <Box component="img" src={imageUrl} alt={label} sx={{ width: "100%", height, objectFit: "cover" }} />
        ) : (
          <Stack alignItems="center" spacing={0.7} sx={{ color: "rgba(25,36,47,.46)" }}>
            <InsertPhotoRoundedIcon sx={{ fontSize: 38 }} />
            <Typography variant="caption">Sin imagen cargada</Typography>
          </Stack>
        )}
        {busy ? (
          <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(255,255,255,.75)", display: "grid", placeItems: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : null}
      </Box>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Button
          size="small"
          variant="outlined"
          disabled={disabled || busy}
          startIcon={<CloudUploadRoundedIcon />}
          onClick={() => inputRef.current?.click()}
          sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}
        >
          {imageUrl ? "Reemplazar" : "Subir imagen"}
        </Button>
        {imageUrl ? (
          <Button
            size="small"
            color="error"
            disabled={disabled || busy}
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={remove}
            sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}
          >
            Eliminar
          </Button>
        ) : null}
      </Stack>

      <input ref={inputRef} type="file" hidden accept={accept} onChange={upload} />
      <Typography variant="caption" sx={{ color: "rgba(25,36,47,.55)" }}>
        {imageUrl ? fileLabelFromAsset(value) : helperText}
      </Typography>
      {error ? <Alert severity="error">{error}</Alert> : null}
    </Stack>
  );
}
